import { useCallback, useContext, useMemo, useState } from "react";
import { Event, EventType, OnboardingFlowType } from "@markit/common.types";
import { useDispatch, useSelector } from "react-redux";
import {
  changeToggleReminderTexts,
  getAccountState,
} from "../../../../redux/slices/accountSlice";
import { ProductItemPreview } from "../../../../components/DisplayItem/ProductItemPreview";
import { HorizontalDivider } from "../../../../components/Dividers/HorizontalDivider";
import { getEventState } from "../../../../redux/slices/eventSlice";
import { getCampaignState } from "../../../../redux/slices/campaignSlice";
import { getSubscribeCampaignAsForm } from "../../../../utils/eventUtils/formUtils";
import {
  hostingDraftEvents,
  isEventRelatedType,
  isProductNonPostType,
  isProductRelatedType,
  sortProductsAndEventsList,
  SUBSCRIBED_CAMPAIGN_NAME,
  userRelatedProducts,
} from "@markit/common.utils";
import EmptyStateButton from "../../../../components/Buttons/EmptyStateButton";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import OnboardingFlowDot from "../../../../components/Onboarding/OnboardingFlowDot";
import AlertContainer from "../../../../components/Containers/AlertContainer";
import { useNavigate } from "../../../../hooks/useNavigate";
import MarkitPlusActionButton from "../../../../components/Subscription/MarkitPlusActionButton";
import { NavigationId } from "../../../../navigation/AppParamList";
import CreatorModePanelWrapper from "../../../../components/CreatorDashboard/CreatorModePanelWrapper";
import { MixpanelContext } from "../../../../context/AnalyticsService";
import SearchBoxContainer from "../../../../components/Containers/SearchBoxContainer";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "../../../../components/DropdownMenu";
import filter from "lodash.filter";
import FlatlistLoadMore from "../../../../components/FlatlistLoadMore";
import { EmptyStateFlatlist } from "../../../../components/EmptyStates/EmptyStateFlatlist";
import StandardBorderedContainer from "../../../../components/Containers/StandardBorderedContainer";
import SwitchToggleItem from "../../../../components/SwitchToggleItem";
import DropdownArrowIcon from "../../../../components/DropdownsAndTabs/DropdownArrowIcon";
import RectangleButtonCreatorPanel from "../../../../components/Buttons/RectangleButtonCreatorPanel";

enum ProductFilterState {
  ALL = "All",
  POSTS = "Posts",
  EVENTS = "Events",
  PRODUCTS = "Products",
  FORMS = "Forms",
}

enum ProductTabValue {
  ACTIVE = 0,
  INACTIVE = 1,
  DRAFTS = 2,
  SETTINGS = 3,
}

const ProductsPanel = () => {
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const { events: eventList } = useSelector(getEventState);
  const { events, tickets } = eventList;
  const { campaigns } = useSelector(getCampaignState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const [value, setValue] = useState<ProductTabValue>(ProductTabValue.ACTIVE);
  const [alertText, setAlertText] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterState, setFilterState] = useState<ProductFilterState>(
    ProductFilterState.ALL
  );
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);

  const styles = {
    filterContainer: {
      gap: 10,
      borderRadius: 8,
      paddingInline: 14,
      paddingBlock: 12,
      backgroundColor: Colors.WHITE1,
      border: `0.8px solid ${Colors.GRAY2}`,
    },
  };

  // The subscribe form has no real Event document of its own - it's synthesized from the
  // "Clicked Subscribe" campaign, so it sorts by that campaign's updatedAt (not a createdAt of its own)
  const subscribeCampaign = useMemo(
    () =>
      campaigns.find((campaign) => campaign.title === SUBSCRIBED_CAMPAIGN_NAME),
    [campaigns]
  );

  const subscribeCampaignAsForm = useMemo(() => {
    const form = getSubscribeCampaignAsForm(
      accountData.uid,
      accountData.profilePicURL,
      accountData.isAdmin
    );
    return subscribeCampaign
      ? { ...form, createdAt: subscribeCampaign.updatedAt }
      : form;
  }, [
    accountData.uid,
    accountData.profilePicURL,
    accountData.isAdmin,
    subscribeCampaign,
  ]);

  const userProducts = useMemo(() => {
    const relatedProducts = userRelatedProducts(
      events,
      accountData.uid,
      tickets
    ).filter(
      (products) =>
        isProductRelatedType(products.eventType) ||
        products.eventType === EventType.GENERIC_LINK
    );
    return [subscribeCampaignAsForm, ...relatedProducts];
  }, [accountData.uid, events, tickets, subscribeCampaignAsForm]);

  // Current user products filtered by active status, not draft, and sorted:
  const currentUserProducts = useMemo(() => {
    return sortProductsAndEventsList(userProducts, "active");
  }, [userProducts]);

  // Past user products filtered by inactive status and are not drafts
  const pastUserProducts = useMemo(() => {
    return sortProductsAndEventsList(userProducts, "inactive");
  }, [userProducts]);

  const draftUserProducts = useMemo(
    () => hostingDraftEvents(events, accountData.uid, tickets),
    [accountData.uid, events, tickets]
  );

  const newProductOnPress = useCallback(
    (sourceLocation: string) => {
      if (mixpanel) {
        mixpanel.track("Pressed Create New Product", {
          distinct_id: accountData.uid,
          button_location: sourceLocation,
        });
      }
      navigate(NavigationId.NEW_PRODUCT);
    },
    [navigate, mixpanel, accountData.uid]
  );

  const toggleReminderTextsOnPress = useCallback(() => {
    dispatch(
      changeToggleReminderTexts(
        accountData.uid,
        !accountData.toggleReminderTexts
      )
    );
  }, [dispatch, accountData.uid, accountData.toggleReminderTexts]);

  const contains = (product: Event, query: string) => {
    return product.title.toLowerCase().includes(query);
  };

  const handleSearch = (text: string) => {
    setSearchTerm(text.toLowerCase());
  };

  const productTabItems = useMemo(
    () =>
      value === ProductTabValue.ACTIVE
        ? currentUserProducts
        : value === ProductTabValue.INACTIVE
          ? pastUserProducts
          : draftUserProducts,
    [value, currentUserProducts, pastUserProducts, draftUserProducts]
  );

  const matchesFilterState = useCallback(
    (product: Event) => {
      if (filterState === ProductFilterState.EVENTS) {
        return isEventRelatedType(product.eventType);
      }
      if (filterState === ProductFilterState.POSTS) {
        return product.eventType === EventType.POST;
      }
      if (filterState === ProductFilterState.PRODUCTS) {
        return isProductNonPostType(product.eventType);
      }
      if (filterState === ProductFilterState.FORMS) {
        return product.eventType === EventType.GENERIC_LINK;
      }
      return true;
    },
    [filterState]
  );

  const filteredProducts = useMemo(
    () => productTabItems.filter(matchesFilterState),
    [productTabItems, matchesFilterState]
  );

  // Tab counts should reflect the currently selected filter, not the total
  // across all product types
  const filteredActiveCount = useMemo(
    () => currentUserProducts.filter(matchesFilterState).length,
    [currentUserProducts, matchesFilterState]
  );
  const filteredInactiveCount = useMemo(
    () => pastUserProducts.filter(matchesFilterState).length,
    [pastUserProducts, matchesFilterState]
  );
  const filteredDraftsCount = useMemo(
    () => draftUserProducts.filter(matchesFilterState).length,
    [draftUserProducts, matchesFilterState]
  );

  const currentItemsToShow: Event[] = useMemo(() => {
    let items: Event[] = filteredProducts;
    if (searchTerm !== "") {
      items = filter(items, (product: Event) => {
        return contains(product, searchTerm);
      });
    }
    return items;
  }, [filteredProducts, searchTerm]);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: ProductFilterState.ALL,
        icon:
          filterState === ProductFilterState.ALL ? "ion:checkmark" : undefined,
        key: "all_products",
        onPress: () => setFilterState(ProductFilterState.ALL),
      },
      {
        title: ProductFilterState.POSTS,
        icon:
          filterState === ProductFilterState.POSTS
            ? "ion:checkmark"
            : undefined,
        key: "posts",
        onPress: () => setFilterState(ProductFilterState.POSTS),
      },
      {
        title: ProductFilterState.PRODUCTS,
        icon:
          filterState === ProductFilterState.PRODUCTS
            ? "ion:checkmark"
            : undefined,
        key: "products",
        onPress: () => setFilterState(ProductFilterState.PRODUCTS),
      },
      {
        title: ProductFilterState.EVENTS,
        icon:
          filterState === ProductFilterState.EVENTS
            ? "ion:checkmark"
            : undefined,
        key: "events",
        onPress: () => setFilterState(ProductFilterState.EVENTS),
      },
      {
        title: ProductFilterState.FORMS,
        icon:
          filterState === ProductFilterState.FORMS
            ? "ion:checkmark"
            : undefined,
        key: "forms",
        onPress: () => setFilterState(ProductFilterState.FORMS),
      },
    ],
    [filterState]
  );

  const renderSubPanel = useMemo(() => {
    return (
      <div className="ColumnNormal" style={{ gap: "14px", paddingBottom: 100 }}>
        <FlatlistLoadMore
          fullList={filteredProducts}
          currentList={currentItemsToShow}
          renderItem={(item) => (
            <div key={item.id}>
              <ProductItemPreview
                item={item}
                isProfileForm={item.title === "Profile Subscribe Form"}
              />
              <HorizontalDivider altMargin={20} />
            </div>
          )}
          renderWhenEmpty={
            <EmptyStateFlatlist
              searchTerm={searchTerm}
              isLoading={false}
              containerStyles={{ paddingTop: 120 }}
              nonSearchEmptyView={
                filterState === ProductFilterState.ALL ? (
                  <EmptyStateButton
                    title={
                      userProducts.length === 0
                        ? "Create Your First Product"
                        : value === ProductTabValue.ACTIVE
                          ? "No Active Products"
                          : value === ProductTabValue.INACTIVE
                            ? "No Past Products"
                            : "No Product Drafts"
                    }
                    description={
                      userProducts.length === 0
                        ? "Products can be events, newsletters, pdfs, images, videos, webinars, and more"
                        : ""
                    }
                    icon={
                      <Icon
                        icon={"ion:bag-handle"}
                        height={50}
                        style={{ color: Colors.PINK2 }}
                      />
                    }
                    iconBackgroundColor={Colors.PINK2 + "1a"}
                    iconBox={80}
                    btnText="New Product"
                    onPress={() =>
                      newProductOnPress("Products Panel Empty State")
                    }
                    containerStyles={{ paddingTop: 200 }}
                  />
                ) : (
                  <EmptyStateButton
                    title={"No Results Found"}
                    description={
                      "There are no products found within this filter"
                    }
                    icon={<Icon icon={"ion:search"} height={60} />}
                    iconBox={70}
                    containerStyles={{ paddingTop: 200 }}
                  />
                )
              }
            />
          }
          isLoadingMore={false}
          showLoadMore={false}
          loadMoreOnPress={() => {}}
        />
      </div>
    );
  }, [
    filteredProducts,
    currentItemsToShow,
    searchTerm,
    filterState,
    userProducts.length,
    value,
    newProductOnPress,
  ]);

  return (
    <CreatorModePanelWrapper
      title="Products"
      headerRight={
        <div className="AlignedRow" style={{ gap: 14 }}>
          <MarkitPlusActionButton />
          {/* <FatHeaderActionButton
            title="Export Data"
            onPress={exportSpreadsheetOnPress}
            loading={downloading}
          /> */}
          <RectangleButtonCreatorPanel
            title="New Product"
            iconName="ion:add-circle"
            rightIcon={
              <OnboardingFlowDot
                flowTypes={[
                  OnboardingFlowType.SELL_PRODUCTS,
                  OnboardingFlowType.NEWSLETTER_BLOG,
                  OnboardingFlowType.ORGANIZE_EVENTS,
                ]}
              />
            }
            onPress={() => newProductOnPress("Products Panel Header")}
          />
        </div>
      }
      headerTabs={{
        tabNames: [
          `Active (${filteredActiveCount})`,
          `Inactive (${filteredInactiveCount})`,
          `Drafts (${filteredDraftsCount})`,
          "Settings",
        ],
        tabValue: value,
        setTabValue: setValue,
      }}
    >
      <div
        className="ColumnNormal"
        style={{
          padding: 30,
          height: "100%",
          backgroundColor:
            value === ProductTabValue.SETTINGS ? Colors.WHITE1 : Colors.WHITE,
          gap: 24,
        }}
      >
        {value === ProductTabValue.SETTINGS ? (
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <span className="sectionTitle">Event Defaults</span>
            <StandardBorderedContainer
              containerStyles={{
                padding: 14,
                backgroundColor: Colors.WHITE,
                width: 400,
              }}
            >
              <SwitchToggleItem
                title="Send Ticket Reminder Texts"
                description="Send an automatic text one hour before the event starts with the guest's ticket link."
                toggleValue={
                  accountData.toggleReminderTexts !== undefined
                    ? accountData.toggleReminderTexts
                    : true
                }
                onChange={toggleReminderTextsOnPress}
              />
            </StandardBorderedContainer>
          </div>
        ) : (
          <>
            <div className="AlignedRow" style={{ gap: 14 }}>
              <SearchBoxContainer
                placeholder={"Search"}
                onChange={(e) => handleSearch(e.target.value)}
                containerStyles={{ marginTop: 0, width: 394 }}
              />
              <DropdownMenu
                dropdownItems={dropdownItems}
                trigger={["click"]}
                placement="bottomLeft"
                onOpenChange={(isOpen: boolean) => setDropdownVisible(isOpen)}
                altWidth={150}
              >
                <div
                  className="AlignedRowSelect"
                  style={styles.filterContainer}
                >
                  <span className="bodyMedium">{filterState}</span>
                  <DropdownArrowIcon isOpen={dropdownVisible} height={14} />
                </div>
              </DropdownMenu>
            </div>
            {renderSubPanel}
          </>
        )}
      </div>
      <AlertContainer
        headerComp={alertText}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
    </CreatorModePanelWrapper>
  );
};

export default ProductsPanel;
